import type { FileManagerPermission } from '@/composeables/file-manager-capabilities';
import {
  useFileManagerMounts,
  type FileManagerMountsState,
} from '../logic/core/useFileManagerMounts';

export type FileManagerPermissionsState = {
  /**
   * What this user may do at `path` — the capabilities the host stamped onto whichever mount the path
   * lives under, and `[]` for a path under no mount (the virtual root, an intermediate namespace, an
   * unknown path). Granting nothing there is correct rather than merely safe: there is no drive behind
   * such a path for any of these verbs to act on.
   */
  permissionsAt: (path: string) => FileManagerPermission[];
};

/**
 * Path-scoped, because the pane can show several projects at once and a user's `files` level is per
 * project (D6). There is deliberately no single ambient `permissions` ref any more: one would have to
 * pick a project, and every read site already has the path it means in scope — an item's own path, the
 * current folder's, or (for a drop) both sides'.
 *
 * No provide/inject of its own: the capabilities ride on the mount table, which is already provided.
 * `fileManagerPermissionsFor` is the form for a caller that holds the mounts state directly —
 * FileManager.vue's own setup(), which cannot inject its own provide().
 */
export const fileManagerPermissionsFor = (
  mounts: Pick<FileManagerMountsState, 'mountAt'>,
): FileManagerPermissionsState => ({
  permissionsAt: (path: string) => mounts.mountAt(path)?.permissions ?? [],
});

export const useFileManagerPermissions = (): FileManagerPermissionsState =>
  fileManagerPermissionsFor(useFileManagerMounts());
